import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const PencilIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);

const BookOpenIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const SparklesIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const ChatBubbleIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
  </svg>
);

const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const EnvelopeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="flex flex-col items-center text-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-3 tracking-tight leading-tight">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7 max-w-sm mx-auto">{description}</p>
  </div>
);

// ==================== MAIN COMPONENT ====================

const CreativeWriting = () => {
  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Enrichment Program
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                Creative Writing
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Developing writing skills and creative expression through various literary forms
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== ABOUT SECTION ==================== */}
        <section className="py-20 md:py-28 bg-[#F5EFE0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Program Overview"
              title="Express Yourself Through Writing"
              description="Our Creative Writing program helps students develop their voice, explore different genres, and express themselves through the written word"
            />
          </Container>
        </section>

        {/* ==================== FEATURES ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="What Students Learn"
              title="Writing Skills Development"
              description="Building essential communication and creative expression abilities"
            />

            <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
              <FeatureCard
                icon={<PencilIcon />}
                title="Writing Techniques"
                description="Students learn various writing styles, narrative techniques, and literary devices."
              />

              <FeatureCard
                icon={<BookOpenIcon />}
                title="Literary Exploration"
                description="Exploring different genres including poetry, fiction, and creative non-fiction."
              />

              <FeatureCard
                icon={<SparklesIcon />}
                title="Creative Expression"
                description="Developing unique voices and perspectives through imaginative storytelling."
              />

              <FeatureCard
                icon={<ChatBubbleIcon />}
                title="Communication Skills"
                description="Building strong written communication and self-expression abilities."
              />
            </div>
          </Container>
        </section>

        {/* ==================== INSTRUCTOR ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Instructor"
              title="Meet Your Teacher"
              description="Learn from an experienced educator passionate about writing and student creativity"
            />

            <div className="mt-14 max-w-sm mx-auto">
              <div className="flex flex-col items-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-school-gold/20 shadow-sm mb-6">
                  <div className="w-full h-full flex items-center justify-center bg-school-blue/8 text-school-blue">
                    <UserIcon className="w-12 h-12" />
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-2 tracking-tight text-center">
                  Melissa Berkowitz
                </h3>
                <p className="text-base text-school-goldDark font-medium mb-4">Creative Writing Teacher</p>
                <a
                  href="/coming-soon"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-school-blue/8 text-school-blue text-sm font-semibold hover:bg-school-blue hover:text-white transition-colors"
                >
                  <EnvelopeIcon />
                  Send Email
                </a>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Explore More Programs
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Discover other enrichment opportunities available at PS 85
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/enrichment"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                All Enrichment Programs
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue font-medium border-2 border-school-blue/20 hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default CreativeWriting;
